// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Appointment, DocumentReference, Encounter, Provenance } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getTelehealthInfo } from '@polarissleep/scheduling';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAppointmentTypeDisplay,
  getPatientFromAppointment,
  getProviderFromAppointment,
  resolveProviderName,
} from '../utils/appointment-formatting';

/** Enriched provider-side appointment with related FHIR data */
export interface ProviderAppointmentDetails {
  appointment: Appointment;
  encounter: Encounter | undefined;
  patientName: string;
  patientId: string;
  providerName: string;
  providerId: string;
  appointmentType: string;
  joinUrl: string | undefined;
  documents: { title: string; id: string }[];
  /** ISO string of when the encounter completed */
  completedAt: string | undefined;
  /** Whether the encounter note has been completed or signed */
  hasNote: boolean;
  /** Whether the encounter note has actually been signed (a Provenance targets the Encounter) */
  noteSigned: boolean;
  /** Days since the encounter completed without a signed note */
  timeOverdueDays: number | undefined;
  /** Truncated comment/note from the encounter or composition */
  comment: string | undefined;
}

/**
 * Enriches a single Appointment with Encounter, DocumentReference, and note status data.
 */
async function enrichAppointment(
  medplum: ReturnType<typeof useMedplum>,
  appointment: Appointment
): Promise<ProviderAppointmentDetails> {
  const patient = getPatientFromAppointment(appointment);
  const provider = getProviderFromAppointment(appointment);
  const providerName = await resolveProviderName(medplum, appointment, provider.name);
  const appointmentType = getAppointmentTypeDisplay(appointment);
  const telehealthInfo = getTelehealthInfo(appointment);

  const base: ProviderAppointmentDetails = {
    appointment,
    encounter: undefined,
    patientName: patient.name,
    patientId: patient.id,
    providerName,
    providerId: provider.id,
    appointmentType,
    joinUrl: telehealthInfo?.joinUrl,
    documents: [],
    completedAt: undefined,
    hasNote: false,
    noteSigned: false,
    timeOverdueDays: undefined,
    comment: undefined,
  };

  if (!appointment.id) {
    return base;
  }

  // Find the Encounter linked to this Appointment
  const encounters = await medplum.searchResources('Encounter', {
    appointment: getReferenceString(appointment),
    _count: '1',
  });

  const encounter = encounters[0];
  if (!encounter?.id) {
    return base;
  }

  base.encounter = encounter;
  base.completedAt = encounter.period?.end;

  // Completed notes are represented by Encounter.status='finished'; signed notes
  // add Provenance records targeting the Encounter.
  const [documentRefs, provenances] = await Promise.all([
    medplum
      .searchResources('DocumentReference', {
        encounter: getReferenceString(encounter),
        _count: '10',
      })
      .catch(() => [] as DocumentReference[]),
    medplum.searchResources('Provenance', `target=${getReferenceString(encounter)}`).catch(() => [] as Provenance[]),
  ]);

  // Collect DocumentReference info
  base.documents = documentRefs
    .filter((doc: DocumentReference) => doc.id)
    .map((doc: DocumentReference) => ({
      title: doc.description ?? doc.type?.text ?? doc.type?.coding?.[0]?.display ?? 'Document',
      id: doc.id as string,
    }));

  // Signing the note creates a Provenance targeting the Encounter (see
  // useEncounterNoteController), so that — and only that — means "signed".
  base.noteSigned = provenances.length > 0;

  // Dashboard should not flag notes already completed in the encounter workflow.
  base.hasNote = encounter.status === 'finished' || base.noteSigned;

  // Extract a comment from the appointment when available.
  if (appointment.comment) {
    base.comment = appointment.comment.length > 50 ? appointment.comment.substring(0, 47) + '...' : appointment.comment;
  }

  // Count the note overdue from when the visit actually ended, falling back to the
  // scheduled end for an appointment that was never concluded (no encounter period
  // end). Gated on `noteSigned`, not `hasNote`: concluding the encounter marks it
  // 'finished' but leaves the note unsigned and still owed.
  const overdueFrom = base.completedAt ?? appointment.end;
  if (overdueFrom && !base.noteSigned) {
    const diffMs = Date.now() - new Date(overdueFrom).getTime();
    if (diffMs > 0) {
      base.timeOverdueDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }
  }

  return base;
}

/**
 * Hook that enriches an array of Appointment resources with provider-relevant data:
 * Encounter, DocumentReferences, note status, and time overdue calculations.
 * @param appointments - Raw FHIR Appointment resources
 * @returns Enriched appointment details, loading state, and error
 */
export function useProviderAppointmentDetails(appointments: Appointment[]): {
  details: ProviderAppointmentDetails[];
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const [details, setDetails] = useState<ProviderAppointmentDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const appointmentsRef = useRef(appointments);
  appointmentsRef.current = appointments;
  const appointmentIds = appointments.map((appointment) => appointment.id ?? '').join(',');

  // Key re-fetches on appointment IDs so state updates from async enrichment do not
  // retrigger the effect when callers provide a new array reference each render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: appointmentIds is the stable re-fetch trigger; appointments are read from a ref.
  const fetchDetails = useCallback(async () => {
    const current = appointmentsRef.current;
    if (current.length === 0) {
      setDetails([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const enriched = await Promise.all(current.map((appt) => enrichAppointment(medplum, appt)));
      setDetails(enriched);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to enrich appointments'));
    } finally {
      setLoading(false);
    }
  }, [medplum, appointmentIds]);

  useEffect(() => {
    fetchDetails().catch(console.error);
  }, [fetchDetails]);

  return { details, loading, error };
}
